if (!customElements.get('wls-sticky-buybox')) {
  class WLSStickyBuybox extends HTMLElement {
    constructor() {
      super();

      this.classes = {
        active: 'active',
        hidden: 'hidden',
        mainProductContainer: 'js-main-product-container',
        buyboxAddToCartButton: 'js-buybox-add-to-cart-button',
        stickyBuybox: 'js-sticky-buybox',
        footer: 'wls-footer',
      };

      // Array of selectors that should hide the sticky buybox when visible
      this.hideConditions = [
        {
          selector: `.${this.classes.buyboxAddToCartButton}`,
          description: 'Buybox button'
        },
        {
          selector: `.${this.classes.footer}`,
          description: 'Footer'
        }
        // Easy to add more conditions by adding objects to this array
        // Example:
        // {
        //   selector: '.some-other-element',
        //   description: 'Some other element'
        // }
      ];

      this.selectors = {
        mainProductContainer: this.closest('wls-main-product')?.querySelector('.js-main-product-container'),
      };
      this.observers = {};
      this.visibilityStates = {};

      this.isStickyActive = false;
    }

    connectedCallback() {
      // Wait for DOM to be ready
      setTimeout(() => {
        this.init();
      }, 0);
    }

    init() {
      if (!this || !this.selectors.mainProductContainer) {
        console.warn('Sticky buybox: Missing required elements');
        return;
      }

      this.setupHideConditions();
      this.setupIntersectionObservers();
      this.bindResizeEvents();
    }

    setupHideConditions() {
      // Initialize visibility states for all hide conditions
      this.hideConditions.forEach((condition, index) => {
        const element = document.querySelector(condition.selector);
        if (element) {
          this.selectors[`condition${index}`] = element;
          this.visibilityStates[`condition${index}`] = false;
        } else {
          console.warn(`Sticky buybox: Could not find element for ${condition.description} (${condition.selector})`);
        }
      });
    }

    setupIntersectionObservers() {
      // Create observers for each hide condition with same threshold
      this.hideConditions.forEach((condition, index) => {
        const element = this.selectors[`condition${index}`];
        if (!element) return;

        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              // Consider visible if more than 20% is visible (same for all elements)
              const isVisible = entry.intersectionRatio > 0.2;
              
              this.visibilityStates[`condition${index}`] = isVisible;
              this.updateStickyState();
            });
          },
          {
            threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1.0],
            rootMargin: '0px 0px 0px 0px',
          }
        );

        observer.observe(element);
        this.observers[`condition${index}`] = observer;
      });
    }

    bindResizeEvents() {
      this.handleResize = this.handleResize.bind(this);
      window.addEventListener('resize', this.handleResize, { passive: true });
    }

    handleResize() {
      // Force update sticky state on resize
      this.updateStickyState();
    }

    updateStickyState() {
      // Check if any hide condition is visible
      const anyHideConditionVisible = Object.values(this.visibilityStates).some(state => state === true);

      // Show sticky buybox when NO hide conditions are visible
      const shouldShowSticky = !anyHideConditionVisible;

      if (shouldShowSticky) {
        this.showSticky();
      } else {
        this.hideSticky();
      }
    }

    showSticky() {
      if (!this.classList.contains(this.classes.hidden)) {
        return;
      }
      this.classList.remove(this.classes.hidden);
      this.isStickyActive = true;
    }

    hideSticky() {
      if (this.classList.contains(this.classes.hidden)) {
        return;
      }
      this.classList.add(this.classes.hidden);
      this.isStickyActive = false;
    }

    disconnectedCallback() {
      // Clean up all observers
      Object.values(this.observers).forEach(observer => {
        if (observer) {
          observer.disconnect();
        }
      });

      if (this.handleResize) {
        window.removeEventListener('resize', this.handleResize);
      }
    }
  }

  window.customElements.define('wls-sticky-buybox', WLSStickyBuybox);
}
